import React from "react";
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
  MDBIcon,
  MDBRow,
  MDBCol,
  MDBInput,
} from "mdb-react-ui-kit";

import { doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";

const NUMBERING_SCHEMES = [
  { value: "LEFT_TO_RIGHT", label: "Izquierda a derecha" },
  { value: "RIGHT_TO_LEFT", label: "Derecha a izquierda" },
  { value: "BY_ROW_LABEL", label: "Por fila (A1, A2… B1, B2…)" },
  { value: "CONTINUOUS", label: "Continuo (sin filas)" },
  { value: "MANUAL", label: "Manual (asignar manualmente)" },
];

const PALETTE = [
  "#3b82f6", "#ef4444", "#10b981", "#f59e0b",
  "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16",
  "#f97316", "#6366f1",
];

/**
 * ZoneEditorModal — crear o editar una zona dentro de una sala.
 *
 * Props:
 *  - show: boolean
 *  - zone: VenueZoneDTO (null = nueva)
 *  - idVenue: integer
 *  - gridRows, gridCols: límites de la cuadrícula
 *  - selectionStart: {row, col} — posición de inicio preseleccionada (opcional)
 *  - selectionEnd: {row, col} — posición de fin preseleccionada (opcional)
 *  - onClose: () => void
 *  - onSaved: (zone) => void
 */
class ZoneEditorModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.initialState(props);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.show !== this.props.show && this.props.show) {
      this.setState(this.initialState(this.props));
    }
  }

  initialState = (props) => {
    const { zone, selectionStart, selectionEnd, gridRows, gridCols } = props;
    if (zone) {
      return {
        name: zone.name || "",
        rowStart: String(zone.rowStart ?? 0),
        colStart: String(zone.colStart ?? 0),
        rowEnd: String(zone.rowEnd ?? 0),
        colEnd: String(zone.colEnd ?? 0),
        numbered: zone.numbered !== false,
        numberingScheme: zone.numberingScheme || "LEFT_TO_RIGHT",
        numberingStart: String(zone.numberingStart ?? 1),
        color: zone.color || PALETTE[0],
        isStage: zone.isStage === true,
        saving: false,
        recalculating: false,
      };
    }
    return {
      name: "",
      rowStart: selectionStart ? String(selectionStart.row) : "0",
      colStart: selectionStart ? String(selectionStart.col) : "0",
      rowEnd: selectionEnd ? String(selectionEnd.row) : String((gridRows || 1) - 1),
      colEnd: selectionEnd ? String(selectionEnd.col) : String((gridCols || 1) - 1),
      numbered: true,
      numberingScheme: "LEFT_TO_RIGHT",
      numberingStart: "1",
      color: PALETTE[0],
      isStage: false,
      saving: false,
      recalculating: false,
    };
  };

  handleClose = () => this.props.onClose && this.props.onClose();

  handleRecalculate = () => {
    const { zone } = this.props;
    if (!zone || !zone.idZone) return;
    this.setState({ recalculating: true });
    doFetchJson("TEAN", `recalculateZoneNumbering/${zone.idZone}`, { method: "PUT" })
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          toast.show({ message: json.MESSAGE || "Numeración recalculada", type: "success" });
          this.props.onSaved && this.props.onSaved(json.DATA);
        } else {
          toast.show({ message: (json && json.MESSAGE) || "Error al recalcular", type: "danger" });
        }
      })
      .catch(() => toast.show({ message: "Error de red", type: "danger" }))
      .finally(() => this.setState({ recalculating: false }));
  };

  handleSave = () => {
    const { zone, idVenue } = this.props;
    const { name, rowStart, colStart, rowEnd, colEnd, numbered, numberingScheme, numberingStart, color, isStage } = this.state;

    if (!name.trim()) {
      toast.show({ message: "El nombre de la zona es obligatorio", type: "warning" });
      return;
    }

    const rs = parseInt(rowStart, 10);
    const cs = parseInt(colStart, 10);
    const re = parseInt(rowEnd, 10);
    const ce = parseInt(colEnd, 10);

    if ([rs, cs, re, ce].some(isNaN)) {
      toast.show({ message: "Las coordenadas deben ser números enteros", type: "warning" });
      return;
    }
    if (rs > re || cs > ce) {
      toast.show({ message: "El inicio debe ser menor o igual al fin", type: "warning" });
      return;
    }

    const dto = {
      idZone: zone ? zone.idZone : null,
      idVenue: idVenue,
      name: name.trim(),
      rowStart: rs,
      colStart: cs,
      rowEnd: re,
      colEnd: ce,
      isStage,
      numbered: isStage ? false : numbered,
      numberingScheme: (!isStage && numbered) ? numberingScheme : null,
      numberingStart: (!isStage && numbered) ? parseInt(numberingStart, 10) || 1 : null,
      color: color || null,
    };

    this.setState({ saving: true });
    const endpoint = zone ? "updateZone" : "createZone";
    const method = zone ? "PUT" : "POST";
    doFetchJson("TEAN", endpoint, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(dto) })
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          toast.show({ message: zone ? "Zona actualizada" : "Zona creada", type: "success" });
          this.props.onSaved && this.props.onSaved(json.DATA);
        } else {
          toast.show({ message: (json && json.MESSAGE) || "Error al guardar la zona", type: "danger" });
        }
      })
      .catch(() => toast.show({ message: "Error de red", type: "danger" }))
      .finally(() => this.setState({ saving: false }));
  };

  render() {
    const { show, zone, gridRows, gridCols } = this.props;
    const { name, rowStart, colStart, rowEnd, colEnd, numbered, numberingScheme, numberingStart, color, isStage, saving, recalculating } = this.state;
    const maxRow = (gridRows || 1) - 1;
    const maxCol = (gridCols || 1) - 1;

    return (
      <MDBModal show={show} onHide={this.handleClose} tabIndex="-1">
        <MDBModalDialog centered>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                <MDBIcon fas icon="vector-square" className="me-2" />
                {zone ? "Editar zona" : "Nueva zona"}
              </MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={this.handleClose} />
            </MDBModalHeader>
            <MDBModalBody>
              <MDBRow className="g-3">
                {/* Nombre */}
                <MDBCol size="12">
                  <MDBInput
                    label="Nombre de la zona *"
                    value={name}
                    onChange={(e) => this.setState({ name: e.target.value })}
                  />
                </MDBCol>

                {/* Coordenadas */}
                <MDBCol size="12">
                  <div className="text-muted small mb-2">
                    Posición en la cuadrícula (0-indexado, fila/columna).
                    Máximo: fila {maxRow}, columna {maxCol}.
                  </div>
                </MDBCol>
                <MDBCol size="6">
                  <MDBInput
                    label="Fila inicio"
                    type="number"
                    min="0"
                    max={maxRow}
                    value={rowStart}
                    onChange={(e) => this.setState({ rowStart: e.target.value })}
                  />
                </MDBCol>
                <MDBCol size="6">
                  <MDBInput
                    label="Columna inicio"
                    type="number"
                    min="0"
                    max={maxCol}
                    value={colStart}
                    onChange={(e) => this.setState({ colStart: e.target.value })}
                  />
                </MDBCol>
                <MDBCol size="6">
                  <MDBInput
                    label="Fila fin"
                    type="number"
                    min="0"
                    max={maxRow}
                    value={rowEnd}
                    onChange={(e) => this.setState({ rowEnd: e.target.value })}
                  />
                </MDBCol>
                <MDBCol size="6">
                  <MDBInput
                    label="Columna fin"
                    type="number"
                    min="0"
                    max={maxCol}
                    value={colEnd}
                    onChange={(e) => this.setState({ colEnd: e.target.value })}
                  />
                </MDBCol>

                {/* Color */}
                <MDBCol size="12">
                  <div className="form-check mb-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="zone-is-stage"
                      checked={isStage}
                      onChange={(e) => this.setState({ isStage: e.target.checked })}
                    />
                    <label className="form-check-label fw-semibold" htmlFor="zone-is-stage">
                      <MDBIcon fas icon="theater-masks" className="me-1 text-dark" />
                      Zona escenario
                    </label>
                    {isStage && (
                      <div className="text-muted small mt-1">
                        La zona se mostrará como un bloque sólido y no se podrá asignar a lotes de entradas.
                      </div>
                    )}
                  </div>
                </MDBCol>

                {/* Color */}
                <MDBCol size="12">
                  <label className="form-label small text-muted mb-1">Color de zona</label>
                  <div className="d-flex flex-wrap gap-2">
                    {PALETTE.map((c) => (
                      <button
                        key={c}
                        type="button"
                        className={`zone-color-btn${color === c ? " zone-color-btn--active" : ""}`}
                        style={{ backgroundColor: c }}
                        onClick={() => this.setState({ color: c })}
                        title={c}
                      />
                    ))}
                    <input
                      type="color"
                      value={color || "#3b82f6"}
                      onChange={(e) => this.setState({ color: e.target.value })}
                      className="zone-color-custom"
                      title="Color personalizado"
                    />
                  </div>
                </MDBCol>

                {/* Numeración (oculta si zona escenario) */}
                {!isStage && (
                <MDBCol size="12">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="zone-numbered"
                      checked={numbered}
                      onChange={(e) => this.setState({ numbered: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="zone-numbered">
                      Butacas numeradas
                    </label>
                  </div>
                </MDBCol>
                )}

                {!isStage && numbered && (
                  <>
                    <MDBCol size="8">
                      <label className="form-label small text-muted mb-1">Esquema de numeración</label>
                      <select
                        className="form-select form-select-sm"
                        value={numberingScheme}
                        onChange={(e) => this.setState({ numberingScheme: e.target.value })}
                      >
                        {NUMBERING_SCHEMES.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </MDBCol>
                    <MDBCol size="4">
                      <MDBInput
                        label="Nº inicial"
                        type="number"
                        min="0"
                        value={numberingStart}
                        onChange={(e) => this.setState({ numberingStart: e.target.value })}
                      />
                    </MDBCol>
                    {zone && (
                      <MDBCol size="12">
                        <MDBBtn
                          color="info"
                          outline
                          size="sm"
                          onClick={this.handleRecalculate}
                          disabled={recalculating || saving}
                        >
                          {recalculating ? (
                            <span className="spinner-border spinner-border-sm me-1" />
                          ) : (
                            <MDBIcon fas icon="sort-numeric-up" className="me-1" />
                          )}
                          Recalcular numeración
                        </MDBBtn>
                        <div className="text-muted small mt-1">
                          Reasigna los números de butaca según el esquema y número de inicio guardados.
                        </div>
                      </MDBCol>
                    )}
                  </>
                )}
              </MDBRow>
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" outline onClick={this.handleClose} disabled={saving || recalculating}>
                Cancelar
              </MDBBtn>
              <MDBBtn color="primary" onClick={this.handleSave} disabled={saving || recalculating}>
                {saving ? (
                  <span className="spinner-border spinner-border-sm me-1" />
                ) : (
                  <MDBIcon fas icon="save" className="me-1" />
                )}
                {zone ? "Guardar cambios" : "Crear zona"}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    );
  }
}

export default ZoneEditorModal;
